// ROOM 404: Room Directory & Creation Modal

import React, { useEffect, useState } from 'react';
import {
  Gamepad2,
  Lock,
  MessageSquare,
  Plus,
  Radio,
  Search,
  Shield,
  Sparkles,
  Users,
} from 'lucide-react';
import { apiRequest } from '../services/api';
import type { Room } from '../types';
import { DirectMessages } from './DirectMessages';

interface RoomsProps {
  onSelectRoom: (roomId: string) => void;
  onOpenReport?: (targetId?: string) => void;
}

const CATEGORIES = [
  { id: 'all', label: 'All' },
  { id: 'official', label: 'Official' },
  { id: 'coding', label: 'Coding' },
  { id: 'gaming', label: 'Gaming' },
  { id: 'creative', label: 'Creative' },
  { id: 'football', label: 'Football' },
  { id: 'photography', label: 'Photos' },
  { id: 'movie_plans', label: 'Cinema' },
  { id: 'food_explorers', label: 'Food' },
];

export const Rooms: React.FC<RoomsProps> = ({ onSelectRoom, onOpenReport }) => {
  const [chatMode, setChatMode] = useState<'rooms' | 'dm'>('rooms');
  const [rooms, setRooms] = useState<Room[]>([]);
  const [category, setCategory] = useState('all');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);

  // Create room form state
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [roomCat, setRoomCat] = useState('gaming');
  const [slowMode, setSlowMode] = useState(0);
  const [createLoading, setCreateLoading] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);

  const fetchRooms = async () => {
    try {
      const data = await apiRequest<{ rooms: Room[] }>('/api/rooms');
      setRooms(data.rooms);
    } catch {
      // Ignored
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRooms();
  }, []);

  const handleCreateRoom = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;

    setCreateLoading(true);
    setCreateError(null);

    try {
      const result = await apiRequest<{ success: boolean; room: Room }>('/api/rooms', {
        method: 'POST',
        body: {
          name: name.trim(),
          description: description.trim() || undefined,
          category: roomCat,
          slowModeSeconds: Number(slowMode),
        },
      });

      setShowCreateModal(false);
      setName('');
      setDescription('');
      fetchRooms();
      onSelectRoom(result.room.id);
    } catch (err: any) {
      setCreateError(err.message || 'Failed to create room');
    } finally {
      setCreateLoading(false);
    }
  };

  const filteredRooms = rooms.filter((r) => {
    const matchesCategory =
      category === 'all'
        ? true
        : category === 'official'
        ? r.type === 'official'
        : r.category === category;
    const matchesSearch =
      r.name.toLowerCase().includes(search.toLowerCase()) ||
      (r.description && r.description.toLowerCase().includes(search.toLowerCase()));
    return matchesCategory && matchesSearch;
  });

  if (chatMode === 'dm') {
    return (
      <div className="pb-20">
        <div className="max-w-md mx-auto px-4 pt-3 pb-1">
          <div className="flex p-1 bg-[#12141c] border border-[#1f2433] rounded-xl text-xs font-semibold mb-3">
            <button
              onClick={() => setChatMode('rooms')}
              className="flex-1 py-1.5 rounded-lg text-gray-400 hover:text-gray-200 transition-all"
            >
              Public Rooms
            </button>
            <button
              onClick={() => setChatMode('dm')}
              className="flex-1 py-1.5 rounded-lg bg-[#1e2333] text-emerald-400 font-bold shadow flex items-center justify-center space-x-1.5 transition-all"
            >
              <Lock className="w-3.5 h-3.5" />
              <span>Direct Messages (24h)</span>
            </button>
          </div>
        </div>
        <DirectMessages
          onBackToRooms={() => setChatMode('rooms')}
          onOpenReport={onOpenReport}
        />
      </div>
    );
  }

  return (
    <div className="space-y-3 pb-24 max-w-md mx-auto px-4 pt-3">
      {/* Mode Switcher */}
      <div className="flex p-1 bg-[#12141c] border border-[#1f2433] rounded-xl text-xs font-semibold">
        <button
          onClick={() => setChatMode('rooms')}
          className="flex-1 py-1.5 rounded-lg bg-[#1e2333] text-white font-bold shadow transition-all"
        >
          Public Rooms
        </button>
        <button
          onClick={() => setChatMode('dm')}
          className="flex-1 py-1.5 rounded-lg text-gray-400 hover:text-gray-200 flex items-center justify-center space-x-1.5 transition-all"
        >
          <Lock className="w-3.5 h-3.5" />
          <span>Direct Messages (24h)</span>
        </button>
      </div>

      {/* Title & Create Room Button */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-bold text-white tracking-tight">Chatrooms</h2>
          <p className="text-xs text-gray-400">Official channels & student communities</p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="px-3 py-1.5 bg-emerald-500 hover:bg-emerald-400 active:scale-95 text-black font-semibold text-xs rounded-xl flex items-center space-x-1 transition-all"
        >
          <Plus className="w-3.5 h-3.5 stroke-[3]" />
          <span>New Room</span>
        </button>
      </div>

      {/* Search Input */}
      <div className="relative">
        <Search className="w-4 h-4 text-gray-500 absolute left-3 top-2.5" />
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search rooms..."
          className="w-full bg-[#12141c] border border-[#1f2433] rounded-xl pl-9 pr-3 py-2 text-xs text-gray-200 placeholder-gray-500 focus:outline-none focus:border-emerald-500"
        />
      </div>

      {/* Category Pills */}
      <div className="flex items-center space-x-1.5 overflow-x-auto pb-1 scrollbar-none">
        {CATEGORIES.map((c) => (
          <button
            key={c.id}
            onClick={() => setCategory(c.id)}
            className={`px-3 py-1 rounded-lg text-xs whitespace-nowrap transition-colors ${
              category === c.id
                ? 'bg-emerald-500 text-black font-semibold'
                : 'bg-[#12141c] text-gray-400 border border-[#1f2433] hover:text-white'
            }`}
          >
            {c.label}
          </button>
        ))}
      </div>

      {/* Room Cards List */}
      <div className="space-y-2">
        {filteredRooms.map((room) => {
          const isOfficial = room.type === 'official';

          return (
            <div
              key={room.id}
              onClick={() => onSelectRoom(room.id)}
              className="p-3.5 rounded-2xl bg-[#12141c] border border-[#1f2433] hover:border-emerald-500/40 cursor-pointer active:scale-[0.99] transition-all"
            >
              <div className="flex items-center justify-between mb-1">
                <div className="flex items-center space-x-2">
                  <h3 className="text-sm font-semibold text-white tracking-tight">{room.name}</h3>
                  {isOfficial && (
                    <span className="px-1.5 py-0.2 rounded text-[10px] font-mono font-bold bg-cyan-500/10 text-cyan-400 border border-cyan-500/20">
                      OFFICIAL
                    </span>
                  )}
                  {room.visibility === 'invite_only' && (
                    <span title="Invite-only room">
                      <Lock className="w-3 h-3 text-amber-400" />
                    </span>
                  )}
                </div>

                <span className="flex items-center space-x-1 text-xs text-gray-400 font-mono">
                  <Users className="w-3.5 h-3.5 text-gray-500" />
                  <span>{room.member_count}</span>
                </span>
              </div>

              {room.description && (
                <p className="text-xs text-gray-400 line-clamp-1">{room.description}</p>
              )}

              <div className="flex items-center justify-between text-[11px] text-gray-500 font-mono mt-2 pt-2 border-t border-[#1f2433]/60">
                <span className="capitalize">{room.category}</span>
                <span>
                  {room.posting_mode === 'announcement_only'
                    ? 'Broadcast Only'
                    : room.posting_mode === 'slow_mode'
                    ? `Slow mode (${room.slow_mode_seconds}s)`
                    : 'Open Chat'}
                </span>
              </div>
            </div>
          );
        })}
      </div>

      {/* Create Room Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/75 backdrop-blur-sm">
          <div className="w-full max-w-sm bg-[#12141c] border border-[#1f2433] rounded-2xl p-5 shadow-2xl">
            <h3 className="text-base font-bold text-white mb-1">Create Community Room</h3>
            <p className="text-xs text-gray-400 mb-4">
              Verified students can create rooms freely. Reasonable limit: 5 active rooms.
            </p>

            {createError && (
              <div className="p-2.5 rounded-lg bg-red-500/10 border border-red-500/30 text-red-400 text-xs mb-3">
                {createError}
              </div>
            )}

            <form onSubmit={handleCreateRoom} className="space-y-3">
              <div>
                <label className="block text-xs font-medium text-gray-300 mb-1">Room Name</label>
                <input
                  type="text"
                  required
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="e.g. Campus Coders, Anime Club"
                  className="w-full bg-[#090a0f] border border-[#1f2433] rounded-xl px-3 py-2 text-sm text-gray-200 focus:outline-none focus:border-emerald-500"
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-300 mb-1">Description</label>
                <input
                  type="text"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Brief room purpose..."
                  className="w-full bg-[#090a0f] border border-[#1f2433] rounded-xl px-3 py-2 text-sm text-gray-200 focus:outline-none focus:border-emerald-500"
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-300 mb-1">Category</label>
                <select
                  value={roomCat}
                  onChange={(e) => setRoomCat(e.target.value)}
                  className="w-full bg-[#090a0f] border border-[#1f2433] rounded-xl px-3 py-2 text-sm text-gray-200 focus:outline-none focus:border-emerald-500"
                >
                  <option value="gaming">Gaming</option>
                  <option value="coding">Coding</option>
                  <option value="photography">Photography</option>
                  <option value="football">Football</option>
                  <option value="movie_plans">Movie Plans</option>
                  <option value="food_explorers">Food Explorers</option>
                  <option value="creative">Creative</option>
                  <option value="meme">Meme</option>
                  <option value="community">Community</option>
                </select>
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-300 mb-1">Slow Mode</label>
                <select
                  value={slowMode}
                  onChange={(e) => setSlowMode(Number(e.target.value))}
                  className="w-full bg-[#090a0f] border border-[#1f2433] rounded-xl px-3 py-2 text-sm text-gray-200 focus:outline-none focus:border-emerald-500"
                >
                  <option value={0}>Disabled (Instant chat)</option>
                  <option value={5}>5 seconds</option>
                  <option value={15}>15 seconds</option>
                  <option value={30}>30 seconds</option>
                  <option value={60}>60 seconds</option>
                </select>
              </div>

              <div className="flex items-center space-x-2 pt-2">
                <button
                  type="button"
                  onClick={() => setShowCreateModal(false)}
                  className="flex-1 py-2 px-3 bg-[#1f2433] hover:bg-gray-700 text-gray-300 rounded-xl text-xs font-medium"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={createLoading}
                  className="flex-1 py-2 px-3 bg-emerald-500 hover:bg-emerald-400 text-black font-semibold rounded-xl text-xs disabled:opacity-50"
                >
                  {createLoading ? 'Creating...' : 'Create Room'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};
